import { GpsSession, KpiMetric, TaxiLog } from '@/types/taxi';

const TAXI_WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

type ZonePerformance = {
	zone: string;
	pickups: number;
	totalRevenue: number;
	avgRevenue: number;
	lastSeen: string;
};

export const formatTimestamp = (iso: string) => {
	const date = new Date(iso);
	return date.toLocaleString('ja-JP', { hour12: false });
};

export const getZoneFromLog = (log: TaxiLog): string => {
	const match = log.memo.match(/zone:([^\s]+)/i);
	if (match) return match[1];
	if (log.memo.includes('東口')) return '新宿駅東口';
	if (log.memo.includes('西口')) return '新宿駅西口';
	if (log.memo.includes('南口')) return '新宿駅南口';
	if (log.memo.includes('歌舞伎町')) return '歌舞伎町';
	return '新宿駅周辺';
};

const calcDistanceKm = (
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number,
): number => {
	const toRad = (value: number) => (value * Math.PI) / 180;
	const earthRadiusKm = 6371;
	const dLat = toRad(lat2 - lat1);
	const dLon = toRad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(lat1)) *
			Math.cos(toRad(lat2)) *
			Math.sin(dLon / 2) *
			Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return earthRadiusKm * c;
};

export const createGpsSession = (
	startTimestamp: string,
	startLat: number,
	startLon: number,
	endTimestamp: string,
	endLat: number,
	endLon: number,
): GpsSession => {
	const durationMs =
		new Date(endTimestamp).getTime() - new Date(startTimestamp).getTime();
	const durationMinutes = Math.max(0, durationMs / (1000 * 60));
	return {
		id: crypto.randomUUID(),
		startTimestamp,
		endTimestamp,
		startLat,
		startLon,
		endLat,
		endLon,
		durationMinutes: Number(durationMinutes.toFixed(1)),
		distanceKm: Number(calcDistanceKm(startLat, startLon, endLat, endLon).toFixed(2)),
		synced: false,
	};
};

const sum = (values: number[]) => values.reduce((acc, value) => acc + value, 0);

export const buildKpis = (logs: TaxiLog[], gpsSessions: GpsSession[]): KpiMetric[] => {
	const pickups = logs.filter((log) => log.event === 'pickup');
	const dropoffs = logs.filter((log) => log.event === 'dropoff');
	const revenueLogs = logs.filter((log) => typeof log.revenue === 'number');
	const totalRevenue = sum(revenueLogs.map((log) => log.revenue || 0));

	const first = logs[logs.length - 1];
	const last = logs[0];
	const spanHours =
		first && last
			? Math.max(
					(new Date(last.timestamp).getTime() -
						new Date(first.timestamp).getTime()) /
						(1000 * 60 * 60),
					1,
			  )
			: 1;

	const totalIdleMinutes = sum(gpsSessions.map((session) => session.durationMinutes));
	const totalCruiseKm = sum(gpsSessions.map((session) => session.distanceKm));
	const perHour = totalRevenue / spanHours;
	const avgFare =
		pickups.length > 0 ? totalRevenue / pickups.length : 0;
	const idleRatio = spanHours > 0 ? totalIdleMinutes / (spanHours * 60) : 0;
	const captureRate =
		gpsSessions.length > 0 ? pickups.length / gpsSessions.length : pickups.length;

	return [
		{
			id: 'kpi-revenue-hour',
			label: '時間当たり売上',
			value: `¥${Math.round(perHour).toLocaleString()}/h`,
			description: '売上最大化の最重要KPI。シフト時間効率を示す。',
		},
		{
			id: 'kpi-avg-fare',
			label: '平均客単価',
			value: `¥${Math.round(avgFare).toLocaleString()}`,
			description: '高単価エリア・時間帯の発見に使う。',
		},
		{
			id: 'kpi-pickup-count',
			label: '乗車回数',
			value: `${pickups.length} 回`,
			description: '回転率の指標。短時間で回せているかを確認。',
		},
		{
			id: 'kpi-idle-ratio',
			label: '空車時間率',
			value: `${Math.round(idleRatio * 100)}%`,
			description: '待機戦略の優劣を判定。低いほど効率的。',
		},
		{
			id: 'kpi-capture-rate',
			label: '待機→乗車転換率',
			value: `${(captureRate * 100).toFixed(1)}%`,
			description: '滞在判断の精度を示す。待機ごとの成約率。',
		},
		{
			id: 'kpi-cruise-distance',
			label: '待機時移動距離',
			value: `${totalCruiseKm.toFixed(1)} km`,
			description: '無駄な移動コストの把握に使う。',
		},
		{
			id: 'kpi-dropoff-count',
			label: '降車回数',
			value: `${dropoffs.length} 回`,
			description: '入力漏れ確認用。pickupとのバランス監視。',
		},
	];
};

export const buildTopZones = (logs: TaxiLog[]): ZonePerformance[] => {
	const zoneMap = new Map<string, ZonePerformance>();
	for (const log of logs) {
		if (log.event !== 'pickup') continue;
		const zone = getZoneFromLog(log);
		const prev = zoneMap.get(zone);
		const revenue = log.revenue || 0;
		if (!prev) {
			zoneMap.set(zone, {
				zone,
				pickups: 1,
				totalRevenue: revenue,
				avgRevenue: revenue,
				lastSeen: log.timestamp,
			});
			continue;
		}
		const totalRevenue = prev.totalRevenue + revenue;
		const pickups = prev.pickups + 1;
		zoneMap.set(zone, {
			zone,
			pickups,
			totalRevenue,
			avgRevenue: totalRevenue / pickups,
			lastSeen:
				new Date(log.timestamp).getTime() > new Date(prev.lastSeen).getTime()
					? log.timestamp
					: prev.lastSeen,
		});
	}
	return Array.from(zoneMap.values())
		.sort((a, b) => b.totalRevenue - a.totalRevenue)
		.slice(0, 5);
};

export const buildHourlyHeatmap = (logs: TaxiLog[]) => {
	const result = Array.from({ length: 24 }, (_, hour) => ({
		hour,
		pickups: 0,
		revenue: 0,
	}));
	for (const log of logs) {
		const bucket = result[log.hour];
		if (!bucket) continue;
		if (log.event === 'pickup') bucket.pickups += 1;
		bucket.revenue += log.revenue || 0;
	}
	return result;
};

export const toWeekdayLabel = (weekday: number) => TAXI_WEEKDAYS[weekday] || '-';

export const extractDestinationFromSuggestion = (
	suggestion: string,
	fallback = '新宿駅東口',
) => {
	const quoted = suggestion.match(/[「『"]([^」』"]+)[」』"]/);
	if (quoted?.[1]) return quoted[1].trim();

	const nextPattern = suggestion.match(/次は\s*([^\s、。]+)/);
	if (nextPattern?.[1]) return nextPattern[1].trim();

	const atPattern = suggestion.match(/([^\s、。]+)\s*(で|へ)/);
	if (atPattern?.[1]) return atPattern[1].trim();

	return fallback;
};

export const buildGoogleMapsLink = (destination: string) => {
	const query = encodeURIComponent(destination);
	return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

export const generateRuleBasedSuggestion = (logs: TaxiLog[]) => {
	const pickupLogs = logs.filter((log) => log.event === 'pickup');
	if (pickupLogs.length < 3) {
		return 'データが少ないため、まずは「新宿駅東口」で15分待機して反応を見てください。';
	}

	const targetHour = new Date().getHours();
	const targetWeekday = new Date().getDay();
	const recent = pickupLogs.filter(
		(log) => log.hour === targetHour && log.weekday === targetWeekday,
	);
	const source = recent.length > 0 ? recent : pickupLogs;
	const zoneScores = new Map<string, { pickups: number; revenue: number }>();
	for (const log of source) {
		const zone = getZoneFromLog(log);
		const prev = zoneScores.get(zone) || { pickups: 0, revenue: 0 };
		zoneScores.set(zone, {
			pickups: prev.pickups + 1,
			revenue: prev.revenue + (log.revenue || 0),
		});
	}
	const ranked = Array.from(zoneScores.entries())
		.map(([zone, value]) => ({
			zone,
			score: value.pickups * 0.6 + (value.revenue / 10000) * 0.4,
			pickups: value.pickups,
			revenue: value.revenue,
		}))
		.sort((a, b) => b.score - a.score);
	const top = ranked[0];
	if (!top) {
		return '有効データ不足のため、新宿駅東口で短時間待機し次のデータ取得を優先してください。';
	}
	return `次は「${top.zone}」へ。想定根拠: 乗車${top.pickups}件 / 売上¥${Math.round(top.revenue).toLocaleString()}。15分を上限に待機し反応がなければ再移動。`;
};

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const toJstDate = (iso: string) => new Date(new Date(iso).getTime() + JST_OFFSET_MS);
const toJstKey = (iso: string) => {
	const date = toJstDate(iso);
	const year = date.getUTCFullYear();
	const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
	const day = `${date.getUTCDate()}`.padStart(2, '0');
	return `${year}-${month}-${day}`;
};
const getTodayJstKey = () => {
	const now = new Date(Date.now() + JST_OFFSET_MS);
	const year = now.getUTCFullYear();
	const month = `${now.getUTCMonth() + 1}`.padStart(2, '0');
	const day = `${now.getUTCDate()}`.padStart(2, '0');
	return `${year}-${month}-${day}`;
};

export type PerformanceMetrics = {
	revenue: number;
	pickups: number;
	dropoffs: number;
	avgFare: number;
	idleMinutes: number;
	idleRatio: number;
	captureRate: number;
	cruiseKm: number;
	perHourRevenue: number;
	score: number;
};

export type DailyTrendPoint = {
	dateKey: string;
	revenue: number;
	score: number;
};

export type DailyMonthlyAnalytics = {
	todayKey: string;
	today: PerformanceMetrics;
	last30dAverage: PerformanceMetrics;
	deltaScore: number;
	deltaRevenue: number;
	dailyTrend: DailyTrendPoint[];
	topZones30d: ZonePerformance[];
	hourlyHeatmap30d: { hour: number; pickups: number; revenue: number }[];
};

const calcMetrics = (logs: TaxiLog[], gpsSessions: GpsSession[]): PerformanceMetrics => {
	const pickups = logs.filter((log) => log.event === 'pickup');
	const dropoffs = logs.filter((log) => log.event === 'dropoff');
	const totalRevenue = sum(logs.map((log) => log.revenue || 0));
	const avgFare = pickups.length > 0 ? totalRevenue / pickups.length : 0;
	const idleMinutes = sum(gpsSessions.map((session) => session.durationMinutes));
	const cruiseKm = sum(gpsSessions.map((session) => session.distanceKm));
	const captureRate = gpsSessions.length > 0 ? pickups.length / gpsSessions.length : 0;

	const first = logs[logs.length - 1];
	const last = logs[0];
	const spanHours =
		first && last
			? Math.max(
					(new Date(last.timestamp).getTime() -
						new Date(first.timestamp).getTime()) /
						(1000 * 60 * 60),
					1,
			  )
			: 1;
	const perHourRevenue = totalRevenue / spanHours;
	const idleRatio = spanHours > 0 ? idleMinutes / (spanHours * 60) : 0;

	const normalizedRevenue = Math.min(perHourRevenue / 6000, 1) * 40;
	const normalizedPickups = Math.min(pickups.length / 20, 1) * 25;
	const normalizedIdle = (1 - Math.min(idleRatio, 1)) * 20;
	const normalizedCapture = Math.min(captureRate, 1) * 15;
	const score = Math.round(
		normalizedRevenue + normalizedPickups + normalizedIdle + normalizedCapture,
	);

	return {
		revenue: totalRevenue,
		pickups: pickups.length,
		dropoffs: dropoffs.length,
		avgFare,
		idleMinutes,
		idleRatio,
		captureRate,
		cruiseKm,
		perHourRevenue,
		score,
	};
};

const emptyMetrics = (): PerformanceMetrics => ({
	revenue: 0,
	pickups: 0,
	dropoffs: 0,
	avgFare: 0,
	idleMinutes: 0,
	idleRatio: 0,
	captureRate: 0,
	cruiseKm: 0,
	perHourRevenue: 0,
	score: 0,
});

const divideMetrics = (metrics: PerformanceMetrics, divisor: number): PerformanceMetrics => {
	if (divisor <= 0) return emptyMetrics();
	return {
		revenue: metrics.revenue / divisor,
		pickups: metrics.pickups / divisor,
		dropoffs: metrics.dropoffs / divisor,
		avgFare: metrics.avgFare / divisor,
		idleMinutes: metrics.idleMinutes / divisor,
		idleRatio: metrics.idleRatio / divisor,
		captureRate: metrics.captureRate / divisor,
		cruiseKm: metrics.cruiseKm / divisor,
		perHourRevenue: metrics.perHourRevenue / divisor,
		score: metrics.score / divisor,
	};
};

const addMetrics = (acc: PerformanceMetrics, cur: PerformanceMetrics): PerformanceMetrics => ({
	revenue: acc.revenue + cur.revenue,
	pickups: acc.pickups + cur.pickups,
	dropoffs: acc.dropoffs + cur.dropoffs,
	avgFare: acc.avgFare + cur.avgFare,
	idleMinutes: acc.idleMinutes + cur.idleMinutes,
	idleRatio: acc.idleRatio + cur.idleRatio,
	captureRate: acc.captureRate + cur.captureRate,
	cruiseKm: acc.cruiseKm + cur.cruiseKm,
	perHourRevenue: acc.perHourRevenue + cur.perHourRevenue,
	score: acc.score + cur.score,
});

const roundMetrics = (metrics: PerformanceMetrics): PerformanceMetrics => ({
	revenue: Math.round(metrics.revenue),
	pickups: Math.round(metrics.pickups * 10) / 10,
	dropoffs: Math.round(metrics.dropoffs * 10) / 10,
	avgFare: Math.round(metrics.avgFare),
	idleMinutes: Math.round(metrics.idleMinutes * 10) / 10,
	idleRatio: Number(metrics.idleRatio.toFixed(3)),
	captureRate: Number(metrics.captureRate.toFixed(3)),
	cruiseKm: Number(metrics.cruiseKm.toFixed(2)),
	perHourRevenue: Math.round(metrics.perHourRevenue),
	score: Math.round(metrics.score),
});

const isWithinLastDaysJst = (iso: string, days: number) => {
	const now = Date.now() + JST_OFFSET_MS;
	const time = new Date(iso).getTime() + JST_OFFSET_MS;
	return now - time <= days * DAY_MS && now >= time;
};

export const buildDailyMonthlyAnalytics = (
	logs: TaxiLog[],
	gpsSessions: GpsSession[],
): DailyMonthlyAnalytics => {
	const todayKey = getTodayJstKey();
	const logs30d = logs.filter((log) => isWithinLastDaysJst(log.timestamp, 30));
	const gps30d = gpsSessions.filter((session) =>
		isWithinLastDaysJst(session.startTimestamp, 30),
	);
	const logsToday = logs30d.filter((log) => toJstKey(log.timestamp) === todayKey);
	const gpsToday = gps30d.filter((session) => toJstKey(session.startTimestamp) === todayKey);

	const dailyMap = new Map<string, { logs: TaxiLog[]; gps: GpsSession[] }>();
	for (const log of logs30d) {
		const key = toJstKey(log.timestamp);
		const prev = dailyMap.get(key) || { logs: [], gps: [] };
		prev.logs.push(log);
		dailyMap.set(key, prev);
	}
	for (const session of gps30d) {
		const key = toJstKey(session.startTimestamp);
		const prev = dailyMap.get(key) || { logs: [], gps: [] };
		prev.gps.push(session);
		dailyMap.set(key, prev);
	}

	const sortedDailyKeys = Array.from(dailyMap.keys()).sort();
	const dailyTrend = sortedDailyKeys.map((key) => {
		const day = dailyMap.get(key)!;
		const metrics = calcMetrics(day.logs, day.gps);
		return {
			dateKey: key,
			revenue: metrics.revenue,
			score: metrics.score,
		};
	});

	const dailyMetrics = dailyTrend.map((point) => {
		const day = dailyMap.get(point.dateKey)!;
		return calcMetrics(day.logs, day.gps);
	});
	const totalMetrics = dailyMetrics.reduce(addMetrics, emptyMetrics());
	const avgMetrics = divideMetrics(totalMetrics, Math.max(dailyMetrics.length, 1));
	const todayMetrics = calcMetrics(logsToday, gpsToday);

	return {
		todayKey,
		today: roundMetrics(todayMetrics),
		last30dAverage: roundMetrics(avgMetrics),
		deltaScore: Math.round(todayMetrics.score - avgMetrics.score),
		deltaRevenue: Math.round(todayMetrics.revenue - avgMetrics.revenue),
		dailyTrend,
		topZones30d: buildTopZones(logs30d),
		hourlyHeatmap30d: buildHourlyHeatmap(logs30d),
	};
};
